// Interstellar mission scene: Earth → curved animated route → destination planet
// + signal particle traveling along the route. createElement to avoid visual-edits.
import { useRef, useMemo, useEffect, useState, createElement as h } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";

const TEX_DAY = "https://threejs.org/examples/textures/planets/earth_atmos_2048.jpg";
const TEX_CLOUDS = "https://threejs.org/examples/textures/planets/earth_clouds_1024.png";

function useTextures(urls) {
  const [tex, setTex] = useState([]);
  useEffect(() => {
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin("anonymous");
    let cancelled = false;
    const load = (url) => new Promise((res) => loader.load(url, (t) => res(t), undefined, () => res(null)));
    Promise.all(urls.map(load)).then((arr) => {
      if (cancelled) return;
      arr.forEach((t) => { if (t) { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8; } });
      setTex(arr);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, []);
  return tex;
}

function Earth({ position = [0, 0, 0] }) {
  const ref = useRef();
  const clouds = useRef();
  const [day, cloudsTex] = useTextures([TEX_DAY, TEX_CLOUDS]);
  useFrame((_, dt) => {
    if (ref.current) ref.current.rotation.y += dt * 0.07;
    if (clouds.current) clouds.current.rotation.y += dt * 0.09;
  });
  const mat = day
    ? h("meshPhongMaterial", { map: day, shininess: 15, emissive: new THREE.Color("#001428"), emissiveIntensity: 0.2 })
    : h("meshStandardMaterial", { color: "#0a2a44", emissive: "#0d4d6e", emissiveIntensity: 0.4 });
  return h(
    "group",
    { position, rotation: [0.41, 0, 0] },
    h("mesh", { ref }, h("sphereGeometry", { args: [0.42, 48, 48] }), mat),
    cloudsTex
      ? h("mesh", { ref: clouds },
          h("sphereGeometry", { args: [0.428, 48, 48] }),
          h("meshLambertMaterial", { map: cloudsTex, transparent: true, opacity: 0.55, depthWrite: false }))
      : null,
    h("mesh", { scale: [1.12, 1.12, 1.12] },
      h("sphereGeometry", { args: [0.42, 32, 32] }),
      h("meshBasicMaterial", {
        color: "#3FAEFF", transparent: true, opacity: 0.12, side: THREE.BackSide,
        blending: THREE.AdditiveBlending, depthWrite: false,
      })
    )
  );
}

function DestinationPlanet({ position, habitability = 0.8 }) {
  const ref = useRef();
  const color = habitability > 0.8 ? "#62FFB0" : habitability > 0.6 ? "#9CE0FF" : "#FF9F6F";
  useFrame((_, dt) => { if (ref.current) ref.current.rotation.y += dt * 0.4; });
  return h(
    "group",
    { position },
    h("mesh", { ref },
      h("sphereGeometry", { args: [0.32, 36, 36] }),
      h("meshStandardMaterial", { color, emissive: color, emissiveIntensity: 0.6, roughness: 0.6 })
    ),
    h("mesh", { scale: [1.5, 1.5, 1.5] },
      h("sphereGeometry", { args: [0.32, 32, 32] }),
      h("meshBasicMaterial", {
        color, transparent: true, opacity: 0.18, side: THREE.BackSide,
        blending: THREE.AdditiveBlending, depthWrite: false,
      })
    )
  );
}

function StarField({ count = 5000 }) {
  const geo = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 25 + Math.random() * 60;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = r * Math.cos(phi);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    return g;
  }, [count]);
  return h(
    "points",
    { geometry: geo },
    h("pointsMaterial", { size: 0.05, color: "#ffffff", sizeAttenuation: true, transparent: true, opacity: 0.8 })
  );
}

function useRouteCurve(start, end, visual = {}) {
  return useMemo(() => {
    const s = new THREE.Vector3(...start);
    const e = new THREE.Vector3(...end);
    const dir = e.clone().sub(s);
    const amp = visual.wave_amplitude || 1.4;
    const phase = visual.wave_phase || 0;
    const spiral = visual.spiral_factor || 0;
    // Sample multiple control points along the path with sinusoidal modulation
    const ctrls = [s];
    const N = 6;
    for (let i = 1; i < N; i++) {
      const t = i / N;
      const base = s.clone().add(dir.clone().multiplyScalar(t));
      const lateral = new THREE.Vector3(0, 1, 0).cross(dir).normalize();
      const upward = new THREE.Vector3(0, 1, 0);
      const ySwing = Math.sin(phase + t * Math.PI * 2) * amp * 0.5;
      const lSwing = Math.cos(phase + t * Math.PI * 1.7 + spiral * t * 3) * amp * (0.4 + 0.5 * Math.sin(t * Math.PI));
      ctrls.push(base.add(upward.multiplyScalar(ySwing)).add(lateral.multiplyScalar(lSwing)));
    }
    ctrls.push(e);
    return new THREE.CatmullRomCurve3(ctrls, false, "catmullrom", 0.35);
  }, [start[0], start[1], start[2], end[0], end[1], end[2], visual.wave_amplitude, visual.wave_phase, visual.spiral_factor]); // eslint-disable-line
}

function RouteLine({ curve, color = "#00D4FF" }) {
  const obj = useMemo(() => {
    const pts = curve.getPoints(260);
    const g = new THREE.BufferGeometry().setFromPoints(pts);
    const m = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.6 });
    return new THREE.Line(g, m);
  }, [curve, color]);
  return h("primitive", { object: obj });
}

function RouteParticles({ curve, color = "#00D4FF", count = 220 }) {
  const N = count;
  const geo = useMemo(() => {
    const positions = new Float32Array(N * 3);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    return g;
  // eslint-disable-next-line
  }, [N]);
  useFrame(({ clock }) => {
    const positions = geo.attributes.position.array;
    const t = clock.getElapsedTime() * 0.08;
    for (let i = 0; i < N; i++) {
      const u = ((i / N) + t) % 1;
      const p = curve.getPointAt(u);
      positions[i * 3] = p.x;
      positions[i * 3 + 1] = p.y;
      positions[i * 3 + 2] = p.z;
    }
    geo.attributes.position.needsUpdate = true;
  });
  return h(
    "points",
    { geometry: geo },
    h("pointsMaterial", {
      size: 0.07, color, transparent: true, opacity: 0.9,
      sizeAttenuation: true, blending: THREE.AdditiveBlending, depthWrite: false,
    })
  );
}

function SignalHead({ curve, progressRef, color = "#FFFFFF", glow = "#00D4FF" }) {
  const ref = useRef();
  const glowRef = useRef();
  useFrame(({ clock }) => {
    const u = Math.min(0.999, Math.max(0.001, progressRef.current || 0));
    const p = curve.getPointAt(u);
    if (ref.current) ref.current.position.copy(p);
    if (glowRef.current) {
      glowRef.current.position.copy(p);
      const s = 1 + Math.sin(clock.getElapsedTime() * 5) * 0.15;
      glowRef.current.scale.set(s, s, s);
    }
  });
  return h(
    "group",
    null,
    h("mesh", { ref },
      h("sphereGeometry", { args: [0.09, 18, 18] }),
      h("meshBasicMaterial", { color })
    ),
    h("mesh", { ref: glowRef },
      h("sphereGeometry", { args: [0.24, 18, 18] }),
      h("meshBasicMaterial", {
        color: glow, transparent: true, opacity: 0.6,
        blending: THREE.AdditiveBlending, depthWrite: false,
      })
    )
  );
}

function MissionScene({ destination, progressRef, visual = {} }) {
  const start = useMemo(() => [-3.2, 0, 0], []);
  const end = useMemo(() => [3.2, 0.4, -0.6], []);
  const curve = useRouteCurve(start, end, visual);
  const palette = visual.palette || ["#00D4FF", "#62FFB0", "#A077FF"];

  return h(
    "group",
    null,
    h("color", { attach: "background", args: ["#03040B"] }),
    h("ambientLight", { intensity: 0.4 }),
    h("directionalLight", { position: [-6, 2, 4], intensity: 1.4, color: "#FFE9C8" }),
    h("pointLight", { position: [4, 1, -2], intensity: 0.6, color: palette[1] || "#62FFB0" }),
    h(StarField, { count: 5000 }),
    h(Earth, { position: start }),
    h(DestinationPlanet, { position: end, habitability: destination?.habitability ?? 0.8 }),
    h(RouteLine, { curve, color: palette[0] }),
    h(RouteParticles, { curve, color: palette[1] || palette[0], count: visual.particle_density || 220 }),
    h(RouteParticles, { curve, color: palette[2] || palette[0], count: Math.floor((visual.particle_density || 220) * 0.6) }),
    h(SignalHead, { curve, progressRef, color: "#FFFFFF", glow: palette[0] }),
    h(OrbitControls, {
      enablePan: false, enableZoom: true, autoRotate: true, autoRotateSpeed: 0.6,
      rotateSpeed: 0.5, minDistance: 4, maxDistance: 16,
    })
  );
}

export default function CommandScene({ destination, progressRef, visual }) {
  return (
    <Canvas camera={{ position: [0, 2.5, 8], fov: 55 }} dpr={[1, 2]}>
      <MissionScene destination={destination} progressRef={progressRef} visual={visual} />
    </Canvas>
  );
}
